import { createContext, useContext, type Context, type ReactNode } from "react";
import type { Gettext, SprintfArg } from "@absolutesight/gettext";

export interface TextDomainContextValue {
  gettext: (text: string, domain?: string) => string;
  ngettext: (
    single: string,
    plural: string,
    number: number,
    domain?: string,
  ) => string;
  xgettext: (text: string, context: string, domain?: string) => string;
  nxgettext: (
    single: string,
    plural: string,
    number: number,
    context: string,
    domain?: string,
  ) => string;
  __: (text: string, domain?: string) => string;
  _e: (text: string, domain?: string) => string;
  _x: (text: string, context: string, domain?: string) => string;
  _ex: (text: string, context: string, domain?: string) => string;
  _n: (
    single: string,
    plural: string,
    number: number,
    domain?: string,
  ) => string;
  _nx: (
    single: string,
    plural: string,
    number: number,
    context: string,
    domain?: string,
  ) => string;
  sprintf: (format: string, ...args: SprintfArg[]) => string;
  sprintfReact: (format: string, ...args: SprintfArg[]) => ReactNode;
  i18n?: Gettext;
}

export const defaultTextDomainContextValue: TextDomainContextValue = {
  gettext: (text: string) => text,
  ngettext: (single: string, plural: string, number: number) =>
    number === 1 ? single : plural,
  xgettext: (text: string) => text,
  nxgettext: (single: string, plural: string, number: number) =>
    number === 1 ? single : plural,
  __: (text: string) => text,
  _e: (text: string) => text,
  _x: (text: string) => text,
  _ex: (text: string) => text,
  _n: (single: string, plural: string, number: number) =>
    number === 1 ? single : plural,
  _nx: (single: string, plural: string, number: number) =>
    number === 1 ? single : plural,
  sprintf: (format: string) => format,
  sprintfReact: (format: string) => format,
};

export const TextDomainContext: Context<TextDomainContextValue> =
  createContext<TextDomainContextValue>(defaultTextDomainContextValue);

/**
 * Custom hook to access text domain context and translation helpers.
 * @returns {Object} Object containing TextDomainContext and bound translation functions
 */
export function useTextDomainContext(): {
  TextDomainContext: Context<TextDomainContextValue>;
} & TextDomainContextValue {
  const context = useContext(TextDomainContext);
  return {
    TextDomainContext,
    ...context,
  };
}
